import axios from 'axios'
import { getCredentials, setPersistedValue } from '@/lib/utils'
import {
  CRATE_CORE_API_BASE_URL,
  REFRESH_TOKEN_COOKIE_KEY,
  REFRESH_TOKEN_LOCAL_STORAGE_KEY,
  SESSION_TOKEN_COOKIE_KEY,
  SESSION_TOKEN_LOCAL_STORAGE_KEY,
} from '@/lib/config'
import { refreshToken } from '@/lib/crate-core'

const instance = axios.create({
  baseURL: CRATE_CORE_API_BASE_URL,
  withCredentials: false,
})

instance.defaults.headers.common['Content-Type'] = 'application/json'
instance.defaults.headers.common['Accept'] = 'application/json'

// Add JWT token if present in local storage
instance.interceptors.request.use(function (config) {
  const { accessToken } = getCredentials()

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }

  return config
})

// Refresh the access token if the request fails with 401
instance.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    // Check if the error is a 401 or other token related errors.
    if (error.response?.status === 401) {
      console.debug('Unauthorized request!')

      // Check if we're already trying to refresh the access token.
      const isRefreshing: boolean = error.config.url?.includes('refresh-token')
      const shouldRefreshToken: boolean = !isRefreshing

      if (shouldRefreshToken) {
        console.debug('Trying to refresh token...')

        const { refreshToken: persistedRefreshToken } = getCredentials()

        if (persistedRefreshToken) {
          console.debug('Refresh token found!')

          try {
            console.debug('Refreshing token...')
            const { access_token, refresh_token } = await refreshToken(persistedRefreshToken)

            if (access_token && refresh_token) {
              console.debug('Token refreshed!')

              // Update the access token in local storage
              setPersistedValue(SESSION_TOKEN_LOCAL_STORAGE_KEY, SESSION_TOKEN_COOKIE_KEY, access_token)
              setPersistedValue(REFRESH_TOKEN_LOCAL_STORAGE_KEY, REFRESH_TOKEN_COOKIE_KEY, refresh_token)

              // Retry the request with the new access token
              error.config.headers['Authorization'] = `Bearer ${access_token}`
              return instance(error.config)
            }
          } catch (e) {
            console.error('Error refreshing access token', e)
          }
        } else {
          console.debug('No refresh token found!')
        }
      }
    }

    return Promise.reject(error)
  }
)

export default instance
